export const compensationEn = {
    errors: {
        notEnabled: 'Compensation claims are not set up for this organization.',
        claimNotFound: 'Claim not found.',
        profileNotFound: 'This contact has no compensation profile.',
        notContributor: 'Only contributor contacts can receive compensation.',
        notEditable: 'This claim can no longer be changed.',
        notEligible: 'No claim can be created for this contact and period.',
        cadenceLocked:
            "The period length can't be changed while submitted claims or claims with entries overlap the current period of the new length, as the same work could then be claimed twice.",
        claimExists: 'A claim for this contact and period already exists.',
        invalidClaim: 'The claim contains invalid entries.',
        accountRequired: 'Choose an account for expenses and corrections.',
        accountNotFound: 'Account {{account}} does not exist in the chart of accounts.',
        rateInUse: 'Rates assigned to contributors cannot be removed. Deactivate them instead.',
        limitInUse:
            'Allowance limits with an individual amount for a contributor cannot be removed. Remove the amount from the contributor first.',
        invalidLimit: 'Choose an allowance limit from the compensation settings.',
        invalidIndividualLimit: 'An individual amount cannot exceed the amount of its allowance limit.',
        tooManyClaims: 'Select at most {{count}} claims at once.',
        tooManyStatements:
            'Annual statements can be created for at most {{count}} contributors at once. Select a mandate first.',
        approvalFailed: 'The claim could not be approved.',
        negativeTotal: 'Claims with a negative total cannot be approved.',
        ownClaim: 'You cannot approve your own claim. Another person allowed to approve claims has to do it.',
        missingPayee: 'The claim has no bank account for the payout.',
        revokeNotPossible:
            'The approval can no longer be revoked because the receipt was paid or transferred. Add a correction to a later claim instead.',
        costCenterRequired: 'Choose cost center 1. The chart of accounts uses regions, so every receipt needs it.',
        invalidCostCenter: 'Choose a cost center of the selected mandate.',
        costCenterNotBookable: 'Choose a bookable cost center of one of your mandates.',
        fileNotFound: 'The attached file could not be found.',
        deadlineInPast: 'The deadline cannot be in the past.',
        deadlineRequired: 'The current deadline has passed. Set a new one.',
        contactDeleted: "The contributor's contact was deleted, so the claim cannot be booked.",
        payeeNameRequired: 'Enter the account holder of the bank account.',
        payeeBicRequired: 'Enter the BIC for a bank account from another country.',
        returnMessageRequired: 'Tell the contributor what needs to be changed.',
    },
    issues: {
        invalidRate: 'Choose one of the assigned rates.',
        outsidePeriod: 'The date must lie within the claim period.',
        invalidTimeRange: 'The end time must be after the start time.',
        timeOverlap: 'Times on the same day must not overlap.',
        expensesDisabled: 'Expenses are not enabled.',
        expenseMaxAmount: 'The amount exceeds the allowed maximum.',
        expenseReceiptRequired: 'Attach a receipt.',
        payeeRequired: 'Enter the bank account for the payout.',
    },
    notifications: {
        open: 'Open claim',
        review: 'Review claim',
        periodOpenedTitle: 'Your claim for {{period}} is open',
        periodOpenedMessage: 'Record your work and expenses and submit them by {{deadline}}.',
        reminderTitle: 'Your claim for {{period}} is due',
        reminderMessage: 'Please submit it by {{deadline}}.',
        submittedTitle: 'Claim to review',
        submittedMessage: '{{name}} submitted the claim for {{period}}.',
        submittedOnBehalfByMessage: '{{submitter}} submitted the claim of {{name}} for {{period}} on their behalf.',
        returnedTitle: 'Your claim was returned',
        returnedMessage:
            'Your claim for {{period}} needs changes. Check the comments and submit it again by {{deadline}}.',
        approvedTitle: 'Your claim was approved',
        approvedMessage: 'Your claim for {{period}} over {{amount}} was approved.',
        submittedOnBehalfTitle: 'Your claim was submitted for you',
        submittedOnBehalfMessage:
            'Your organization submitted your claim for {{period}} over {{amount}} on your behalf.',
        revokedTitle: 'The approval of your claim was withdrawn',
        revokedMessage: 'Your organization reviews your claim for {{period}} again. Check the comments in the claim.',
        deadlineChangedTitle: 'New deadline for your claim',
        deadlineChangedMessage: 'Please submit your claim for {{period}} by {{deadline}}.',
        changedTitle: 'Your claim was changed',
        changedMessage: 'Your organization changed your claim for {{period}}, which now amounts to {{amount}}.',
        paidTitle: 'Your claim was paid out',
        paidMessage: 'Your organization paid out {{amount}} for your claim for {{period}}.',
        deletedTitle: 'Your claim was deleted',
        deletedMessage: 'Your organization deleted your claim for {{period}}.',
        showClaims: 'Show claims',
        showDocuments: 'Show documents',
        statementTitle: 'Your annual statement {{year}} is available',
        statementMessage: 'Your organization shared the summary of your compensation for {{year}} in your documents.',
        deadlineTitle: 'Submission deadline for {{period}} ends today',
        deadlineMessage: 'Not submitted yet: {{notSubmitted}} · To review: {{toReview}}',
    },
    export: {
        title: 'Compensation {{year}}',
        documentsFileName: 'Compensation claims {{year}}',
        contributor: 'Contributor',
        approvedClaims: 'Approved claims',
        hours: 'Hours',
        allowance: 'Allowances',
        reimbursement: 'Reimbursements',
        payout: 'Approved amount',
        limit: 'Allowance limit',
        limitAmount: 'Amount',
        limitUsed: 'Used',
        limitExempt: 'Tax-free',
        limitTaxable: 'Taxable',
        openClaims: 'Open claims',
        claim: 'Claim',
        period: 'Period',
        statusLabel: 'Status',
        type: 'Type',
        date: 'Date',
        description: 'Description',
        details: 'Details',
        amount: 'Amount',
        types: {
            hours: 'Hours',
            flatRates: 'Flat rate',
            mileage: 'Mileage',
            expenses: 'Expense',
            corrections: 'Correction',
        },
        status: {
            draft: 'Open',
            submitted: 'To review',
            approved: 'Approved',
        },
    },
    statement: {
        documentName: 'Annual compensation statement {{year}}',
        title: 'Annual compensation statement {{year}}',
        provisional: 'Provisional',
        provisionalNotice: 'Provisional: claims for this year can still be submitted.',
        intro: 'Compensation for {{name}} in {{year}}, based on all approved claims:',
        hours: 'Hours worked',
        allowance: 'Allowances',
        reimbursement: 'Reimbursed mileage and expenses',
        payout: 'Total approved',
        limitUsage: '{{used}} of {{amount}}',
        limitTaxable: '{{label}}: taxable part',
        footer: 'Created on {{date}}',
    },
    document: {
        contactDocumentName: 'Claim {{number}} ({{period}})',
        fileName: 'Claim {{number}}.pdf',
        title: 'Compensation claim',
        number: 'Number',
        period: 'Period',
        contributor: 'Contributor',
        payee: 'Bank account',
        hours: 'Hours',
        flatRates: 'Flat rates',
        mileage: 'Mileage',
        expenses: 'Expenses',
        corrections: 'Corrections',
        date: 'Date',
        time: 'Time',
        rate: 'Rate',
        quantity: 'Quantity',
        distance: 'Distance',
        description: 'Description',
        participants: 'Participants',
        amount: 'Amount',
        total: 'Total',
        limits: 'Allowance limits',
        limit: 'Allowance limit',
        limitClaim: 'This claim',
        limitExempt: 'Of which tax-free',
        limitTaxable: 'Of which taxable',
        limitYear: 'Used in {{year}}',
        limitYearValue: '{{used}} of {{amount}}',
        limitNotice:
            'As of approval, with the submitted and approved claims of the year. The annual statement covers all approved claims.',
        declaration: 'Declaration',
        submittedBy: 'Submitted electronically by {{name}} on {{date}}',
        submittedOnBehalf: 'Submitted by {{name}} on behalf of the contributor on {{date}}',
        approvedBy: 'Approved by {{name}} on {{date}}',
        receiptPosition: '{{rate}} – {{period}}',
        expensesPosition: 'Expenses – {{period}}',
        correctionsPosition: 'Corrections – {{period}}',
        receiptDescription: 'Compensation claim {{number}}',
        receiptsNotIncluded: 'These receipts could not be added here and are stored with the claim:',
    },
}
